import { useState, useEffect, useRef } from 'react';
import { useTr } from '../utils/useTr';
import { SUPPORTED_LANGS } from '../utils/i18n';
import { useApp } from '../contexts/AppContext';
import BeeloveLogo from './BeeloveLogo';

function NavIcon({ children, active, size=22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={active ? 'var(--gold)' : 'var(--muted)'}
      strokeWidth={active ? 1.6 : 1.3}
      strokeLinecap="round" strokeLinejoin="round"
      style={{ display:'block', transition:'all 0.2s' }}>
      {children}
    </svg>
  );
}
function IconDiscover({ active }) { return <NavIcon active={active}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88" fill={active ? 'var(--gold)' : 'none'} stroke={active ? 'var(--gold)' : 'var(--muted)'}/></NavIcon>; }
function IconMatches({ active }) { return <NavIcon active={active}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill={active ? 'rgba(201,168,76,0.2)' : 'none'}/></NavIcon>; }
function IconMessages({ active }) { return <NavIcon active={active}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill={active ? 'rgba(201,168,76,0.12)' : 'none'}/></NavIcon>; }
function IconHive({ active }) { return <NavIcon active={active}><path d="M12 2l8.66 5v10L12 22l-8.66-5V7z" fill={active ? 'rgba(201,168,76,0.12)' : 'none'}/><path d="M12 7l4.33 2.5v5L12 17l-4.33-2.5v-5z" fill={active ? 'rgba(201,168,76,0.18)' : 'none'} strokeWidth="1"/></NavIcon>; }
function IconProfile({ active }) { return <NavIcon active={active}><circle cx="12" cy="8" r="3.5" fill={active ? 'rgba(201,168,76,0.15)' : 'none'}/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></NavIcon>; }

function IconEvents({ active }) {
  return <NavIcon active={active}><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" strokeWidth={active?1.8:1.4}/></NavIcon>;
}

function IconCircles({ active }) { return <NavIcon active={active}><circle cx="12" cy="12" r="9" fill={active ? 'rgba(201,168,76,0.12)' : 'none'}/><circle cx="8" cy="10" r="1.8"/><circle cx="16" cy="10" r="1.8"/><circle cx="12" cy="15" r="1.8"/><path d="M8 10 Q12 7 16 10" fill="none" strokeWidth="1.2"/><path d="M8 10 Q10 14 12 15" fill="none" strokeWidth="1.2"/><path d="M16 10 Q14 14 12 15" fill="none" strokeWidth="1.2"/></NavIcon>; }

function IconAgenda({ size=22, color='currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8"  y1="2" x2="8"  y2="6"/>
      <line x1="3"  y1="10" x2="21" y2="10"/>
      <line x1="8"  y1="14" x2="8"  y2="14" strokeWidth="2"/>
      <line x1="12" y1="14" x2="12" y2="14" strokeWidth="2"/>
      <line x1="16" y1="14" x2="16" y2="14" strokeWidth="2"/>
    </svg>
  );
}

function IconBeeConnect({ active }) { return <NavIcon active={active}><circle cx="12" cy="12" r="9" fill={active ? 'rgba(29,158,117,0.15)' : 'none'}/><path d="M12 7l4 3v4l-4 3-4-3v-4z" fill={active ? 'rgba(29,158,117,0.3)' : 'none'} strokeWidth="1.2"/><path d="M12 4v3M20 9l-2.5 1.5M20 15l-2.5-1.5M12 20v-3M4 15l2.5-1.5M4 9l2.5 1.5" strokeWidth="1.1"/></NavIcon>; }

const getNAV = (tr) => [
  { id:'discover',   Icon:IconDiscover,   label:tr('nav.discover') },
  { id:'beehive',    Icon:IconHive,       label:tr('nav.beehive'),    networkFeature:'beehive' },
  { id:'events',     Icon:IconAgenda,     label:tr('nav.events')||'Events',  networkFeature:'events' },
  { id:'scout',      Icon:IconCircles,    label:tr('nav.scout')||'Scout',     networkFeature:'scout' },
  { id:'beeconnect', Icon:IconBeeConnect, label:tr('nav.beeconnect'), networkFeature:'beeconnect' },
  { id:'matches',    Icon:IconMatches,    label:tr('nav.matches')  },
  { id:'messages',   Icon:IconMessages,   label:tr('nav.messages') },
  { id:'profile',    Icon:IconProfile,    label:tr('nav.profile')  },
];

// Network selector shown on all pages for consistency
const FONT_MIN = 0.85; const FONT_MAX = 1.15; const FONT_STEP = 0.05; const FONT_DEFAULT = 1.0;

function useFontScale() {
  const [scale, setScale] = useState(() => {
    try { const s = parseFloat(localStorage.getItem('beeloveFontScale') || FONT_DEFAULT); return isNaN(s) ? FONT_DEFAULT : s; } catch { return FONT_DEFAULT; }
  });
  useEffect(() => {
    document.documentElement.style.fontSize = scale * 16 + 'px';
    try { localStorage.setItem('beeloveFontScale', scale); } catch {}
  }, [scale]);
  return {
    scale,
    increase: () => setScale(s => Math.min(FONT_MAX, parseFloat((s + FONT_STEP).toFixed(2)))),
    decrease: () => setScale(s => Math.max(FONT_MIN, parseFloat((s - FONT_STEP).toFixed(2)))),
    reset:    () => setScale(FONT_DEFAULT),
  };
}

function NetworkSelector({ NETWORKS, activeNetwork, setActiveNetwork, userNetworks }) {
  const tr = useTr();
  const NAV = getNAV(tr);
  const [open, setOpen] = useState(false);
  const net = (NETWORKS || []).find(n => n.id === activeNetwork) || (NETWORKS || [])[0] || { flag:'🌐', label:tr('nav.network') };
  return (
    <div style={{ position:'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display:'flex', alignItems:'center', gap:'0.28rem', padding:'0.2rem 0.45rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-pill)', cursor:'pointer', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.67rem', fontWeight:500 }}>
        <span style={{ fontSize:'0.78rem' }}>{net.flag}</span>
        <span>{net.label}</span>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.5" strokeLinecap="round"><polyline points={open ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}/></svg>
      </button>
      {open && <>
        <div style={{ position:'fixed', inset:0, zIndex:599 }} onClick={() => setOpen(false)}/>
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, zIndex:600, background:'var(--charcoal)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', minWidth:200, boxShadow:'0 8px 32px rgba(0,0,0,0.5)', overflow:'hidden' }}>
          <div style={{ padding:'0.5rem 0.8rem 0.25rem', fontSize:'0.54rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--muted)' }}>{tr('nav.your_networks')}</div>
          {(NETWORKS || []).map(n => {
            const isMember = (userNetworks || []).includes(n.id);
            const isActive = n.id === activeNetwork;
            return (
              <button key={n.id} onClick={() => { if (isMember) { setActiveNetwork(n.id); setOpen(false); } }} style={{ display:'flex', alignItems:'center', gap:'0.5rem', width:'100%', padding:'0.5rem 0.8rem', background: isActive ? 'var(--gold-pale)' : 'transparent', border:'none', borderBottom:'1px solid var(--border)', cursor: isMember ? 'pointer' : 'default', textAlign:'left', opacity: isMember ? 1 : 0.45 }}>
                <span style={{ fontSize:'1rem' }}>{n.flag}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'0.73rem', fontWeight:600, color: isActive ? 'var(--gold)' : 'var(--white)', fontFamily:'Outfit' }}>{n.label}</div>
                  <div style={{ fontSize:'0.57rem', color:'var(--muted)' }}>{n.description}</div>
                </div>
                {isActive && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                {!isMember && <span style={{ fontSize:'0.5rem', color:'var(--muted)', background:'var(--dark)', padding:'0.05rem 0.28rem', borderRadius:'99px', border:'1px solid var(--border)' }}>soon</span>}
              </button>
            );
          })}
          <div style={{ padding:'0.45rem 0.8rem' }}>
            <div style={{ fontSize:'0.57rem', color:'var(--muted)', lineHeight:1.45 }}>{tr('nav.more_cities')}</div>
          </div>
        </div>
      </>}
    </div>
  );
}

export default function Navbar({ onNavigate }) {
  const { page, setPage, goBack, currentUser, unread, matches, theme, toggleTheme, lang, setLang,
          NETWORKS, activeNetwork, setActiveNetwork, userNetworks, features, networkFeatures } = useApp();
  const { scale, increase, decrease } = useFontScale();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    if (!langOpen) return;
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [langOpen]);

  const tr = useTr();
  const NAV = getNAV(tr);
  const nav = t => { setPage(t); if (onNavigate) onNavigate(t); };
  const visibleNav = NAV.filter(item => {
    if (item.networkFeature) {
      // If globally disabled in app_features, hide everywhere
      if (features?.[item.networkFeature] === false) return false;
      // Otherwise require explicit per-network enablement
      return networkFeatures?.[item.networkFeature] === true;
    }
    return true;
  });

  const LANG_LABELS = { en: 'EN', fr: 'FR' };
  const isAdminOrCml = currentUser?.role === 'admin' || currentUser?.role === 'cml';

  const btnBase = { background:'none', border:'1px solid var(--border)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'var(--r-sm)', transition:'all 0.15s', color:'var(--muted)' };
  const iconBtnStyle = { ...btnBase, width:26, height:26, padding:0, flexShrink:0 };

  return (
    <>
      <header style={{ position:'fixed', top:0, left:0, right:0, zIndex:500, height:'var(--nav-h)', background:'var(--nav-bg, rgba(10,10,10,0.97))', backdropFilter:'blur(20px)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div className="nav-inner">
          {/* Left */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.38rem' }}>
            <button onClick={goBack} style={{ background:'none', border:'none', cursor:'pointer', padding:'0.28rem', display:'flex', color:'var(--muted)' }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div onClick={() => nav('discover')} style={{ cursor:'pointer', display:'flex', alignItems:'center' }}>
              <BeeloveLogo variant="compact" height={38} animate={false}/>
            </div>
            {currentUser && NETWORKS?.length > 0 && (
              <NetworkSelector NETWORKS={NETWORKS} activeNetwork={activeNetwork} setActiveNetwork={setActiveNetwork} userNetworks={userNetworks || []}/>
            )}
          </div>

          {/* Right */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.3rem' }}>

            {/* Font size: − and + */}
            <button onClick={decrease} disabled={scale <= FONT_MIN} title={tr('nav.font_label')}
              style={{ ...iconBtnStyle, color: scale<=FONT_MIN ? 'var(--border)' : 'var(--muted)', cursor: scale<=FONT_MIN ? 'default' : 'pointer', fontSize:'1rem', fontWeight:300, lineHeight:1, fontFamily:'Outfit' }}>
              −
            </button>
            <button onClick={increase} disabled={scale >= FONT_MAX} title={tr('nav.font_label')}
              style={{ ...iconBtnStyle, color: scale>=FONT_MAX ? 'var(--border)' : 'var(--muted)', cursor: scale>=FONT_MAX ? 'default' : 'pointer', fontSize:'0.85rem', fontWeight:300, lineHeight:1, fontFamily:'Outfit' }}>
              +
            </button>

            {/* Theme toggle: moon in dark, sun in light */}
            <button onClick={toggleTheme} title={tr('nav.theme_label')}
              style={{ ...iconBtnStyle }}>
              {theme === 'dark'
                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              }
            </button>

            {/* Language button — shows current lang, opens overlay */}
            <div style={{ position:'relative' }} ref={langRef}>
              <button onClick={() => setLangOpen(o => !o)} title={tr('nav.lang_label')}
                style={{ ...btnBase, border:`1px solid ${langOpen ? 'var(--gold-border)' : 'var(--border)'}`, background: langOpen ? 'var(--gold-pale)' : 'none', padding:'0 0.4rem', height:26, fontFamily:'Outfit', fontSize:'0.67rem', fontWeight:700, letterSpacing:'0.06em', color: langOpen ? 'var(--gold)' : 'var(--muted)' }}>
                {LANG_LABELS[lang] || lang.toUpperCase()}
              </button>
              {langOpen && <>
                <div style={{ position:'fixed', inset:0, zIndex:501 }} onClick={() => setLangOpen(false)}/>
                <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:502, background:'var(--charcoal)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', minWidth:120, boxShadow:'0 8px 32px rgba(0,0,0,0.45)', overflow:'hidden' }}>
                  <div style={{ padding:'0.4rem 0.75rem 0.25rem', fontSize:'0.5rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--muted)', fontFamily:'Outfit' }}>{tr('nav.lang_label')}</div>
                  {SUPPORTED_LANGS.map(l => (
                    <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                      style={{ display:'flex', alignItems:'center', gap:'0.45rem', width:'100%', padding:'0.45rem 0.75rem', background: lang===l.code ? 'var(--gold-pale)' : 'transparent', border:'none', borderBottom:'1px solid var(--border)', cursor:'pointer', textAlign:'left' }}>
                      <span style={{ fontFamily:'Outfit', fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.06em', color: lang===l.code ? 'var(--gold)' : 'var(--white)' }}>{LANG_LABELS[l.code] || l.code.toUpperCase()}</span>
                      <span style={{ fontSize:'0.65rem', color:'var(--muted)', flex:1 }}>{l.label}</span>
                      {lang===l.code && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </button>
                  ))}
                </div>
              </>}
            </div>

            {/* Admin / CML direct link */}
            {isAdminOrCml && (
              <button onClick={() => nav(currentUser.role === 'admin' ? 'admin' : 'cml')}
                title={currentUser.role === 'admin' ? 'Admin panel' : 'CML dashboard'}
                style={{ ...iconBtnStyle, border:'1px solid var(--gold-border)', color:'var(--gold)' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </button>
            )}

            {/* Profile avatar — only when logged in */}
            {currentUser && <div onClick={() => nav('profile')} style={{ display:'flex', alignItems:'center', gap:'0.32rem', padding:'0.14rem 0.42rem 0.14rem 0.14rem', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-pill)', cursor:'pointer' }}>
              <div style={{ width:23, height:23, borderRadius:'50%', overflow:'hidden', border:'1px solid var(--gold-border)', flexShrink:0 }}>
                {(currentUser?.photos||'')[0]
                  ? <img src={currentUser?.photos?.[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
                  : <div style={{ width:'100%', height:'100%', background:'var(--dark)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.58rem', color:'var(--gold)' }}>{(currentUser?.name||'')[0]}</div>
                }
              </div>
              <span style={{ fontSize:'0.68rem', fontWeight:600, color:'var(--gold)', fontFamily:'Outfit' }}>{currentUser?.name?.split(' ')[0]}</span>
            </div>}

            {/* Bell */}
            <button onClick={() => nav('notifications')} style={{ background:'none', border:'none', cursor:'pointer', position:'relative', padding:'0.33rem', display:'flex' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={unread>0 ? 'var(--gold)':'var(--muted)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              {unread > 0 && <span style={{ position:'absolute', top:1, right:1, background:'var(--gold)', color:'var(--black)', width:13, height:13, borderRadius:'50%', fontSize:'0.55rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>{unread}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* Bottom nav */}
      <nav style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:500, background:'var(--nav-bg, rgba(10,10,10,0.97))', backdropFilter:'blur(20px)', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'center', height:'var(--bottom-h)', paddingBottom:'env(safe-area-inset-bottom,0px)' }}>
        <div className="nav-inner" style={{ padding:0, width:"100%", maxWidth:"100%" }}>
          {visibleNav.map(({ id, Icon, label }) => {
            const active = page === id || (id === 'events' && page === 'agenda');
            const count  = id === 'matches' ? (matches?.length ?? 0) : 0;
            return (
              <button key={id} onClick={() => nav(id)} style={{ flex:1, minWidth:0, background:'none', border:'none', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'0.16rem', position:'relative', padding:'0 2px' }}>
                {active && <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:22, height:1.5, background:'var(--gold)', borderRadius:1 }}/>}
                <div style={{ position:'relative' }}>
                  <Icon active={active}/>
                  {count > 0 && <span style={{ position:'absolute', top:-4, right:-6, background:'var(--gold)', color:'var(--black)', width:14, height:14, borderRadius:'50%', fontSize:'0.55rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>{count}</span>}
                </div>
                <span style={{ fontSize:'clamp(0.48rem,1.8vw,0.57rem)', fontWeight:active?600:400, letterSpacing:'0.04em', textTransform:'uppercase', color:active?'var(--gold)':'var(--muted)', fontFamily:'Outfit', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%' }}>{tr('nav.'+id, {})||label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="desktop-footer">
        <span>© 2025 BeeLove.me</span>
        {[['beehive','BeeHive'],['beewise','Bee Wise'],['events','Events'],['how','How it works'],['pricing','Pricing'],['about','About'],['privacy','Privacy'],['terms','Terms'],['contact','Contact'],['help','Help'],['safety','Safety']].map(([hash,label]) => (
          <button key={hash} onClick={() => { import('../utils/navigate').then(({ navigate }) => navigate(hash)); }}>{label}</button>
        ))}
        <span style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', color:'var(--gold)', fontSize:'0.65rem' }}>{tr('nav.bee_the_one')}</span>
      </div>
    </>
  );
}
